import { Effect, Layer, ManagedRuntime } from 'effect';

/**
 * A module's runtime, and the one way it runs a single call from outside Effect.
 *
 * Both, because a module needs both. Most of its UI makes one call and awaits it, which is what {@link CallRuntime.runCall} is for. Something that has to manage calls rather than await them — interrupt one that has been overtaken, run one per key, close them all when the screen goes — forks them as fibers instead, and
 * has to fork them in the same runtime, so the fetch client, the account and the tracer are the ones every other call shares rather than a second set.
 */
export interface CallRuntime<Services, Dependencies> {
	/** Acquires what every call is handed. For a caller that forks calls itself, and so builds them in Effect rather than through {@link CallRuntime.runCall}. */
	readonly dependencies: Effect.Effect<Dependencies, never, Services>;
	/**
	 * Runs one call and answers with whatever went wrong rather than hiding it.
	 *
	 * A module's UI is web components, so most calls are made from an `await` rather than from an Effect, and this is how those callers keep what a call failed with: a calculation that could not be asked for because the token was refused is not the same as one the engine could not price, and a row showing a position
	 * has to say which. Every failure is a tagged class, so a caller tells them apart by tag, and tells them from an answer by what an answer is.
	 *
	 * Defects are left alone, and so reject the promise. A thrown exception is a fault in the code rather than a state a component can do anything with, and handing it back as though it were an ordinary failure would bury it.
	 */
	readonly runCall: <A, E, R extends Services>(call: (dependencies: Dependencies) => Effect.Effect<A, E, R>) => Promise<A | E>;
	/** The runtime every call runs in, for a caller that forks its calls as fibers rather than awaiting them one by one. */
	readonly runtime: ManagedRuntime.ManagedRuntime<Services, never>;
}

/**
 * Builds a module's runtime, and the way it runs a single call from outside Effect.
 *
 * Every module needs this and every module needs it to behave the same, which is why it is here rather than written out once per module: how a typed failure crosses the boundary out to a web component is a convention, and a convention that differs per module is worse than no convention at all. What differs per module
 * is only the layer set and what a call is handed, and those are the two arguments.
 *
 * The runtime is built once, when this is called, rather than per call — so the fetch client, the environment lookup and the session storage reader are built once too, and every call shares them, whether it is awaited through `runCall` or forked in the runtime. Anything a layer hands out as an Effect is still read per
 * call: a bearer token that the host app refreshes mid-session has to be, and captured values could not be.
 *
 * Callers that can act on the error channel itself should take the module's own service and stay in Effect.
 *
 * @param layer Everything the runtime provides, which is everything a call may reach for.
 * @param dependencies What every call is handed, acquired from the services the layer provides.
 */
export function makeCallRuntime<Services, Dependencies>(layer: Layer.Layer<Services>, dependencies: Effect.Effect<Dependencies, never, Services>): CallRuntime<Services, Dependencies> {
	const runtime = ManagedRuntime.make(layer);
	return {
		dependencies,
		runCall: (call) =>
			runtime.runPromise(
				dependencies.pipe(
					Effect.flatMap(call),
					Effect.catch((error) => Effect.succeed(error)),
				),
			),
		runtime,
	};
}

/**
 * Builds the one way a module runs a single call from outside Effect.
 *
 * {@link makeCallRuntime} without the runtime, for a module that only ever awaits its calls. See {@link CallRuntime.runCall} for how a failure crosses the boundary.
 *
 * @param layer Everything the runtime provides, which is everything a call may reach for.
 * @param dependencies What every call is handed, acquired from the services the layer provides.
 */
export function makeRunCall<Services, Dependencies>(layer: Layer.Layer<Services>, dependencies: Effect.Effect<Dependencies, never, Services>): <A, E, R extends Services>(call: (dependencies: Dependencies) => Effect.Effect<A, E, R>) => Promise<A | E> {
	return makeCallRuntime(layer, dependencies).runCall;
}
